import React, { FocusEvent, useContext, useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import uuid from 'uuid-random';
import { WordsPageContext, ActionTypes } from '../../../../contexts/wordsPageContext';
import { WordsImage } from '../../../WordsImage';
import { DraggableLabel } from '../../../DraggableLabel';
import {
  getImagesToShow,
  getLabelsToShow,
  getStageColors,
  getVerbWidth,
  isDraggableElementWasMoved,
} from '../../../../utils';
import { WordsWrapper } from '../../../Styled';
import { getCollidedLabel, getExemplarImageURL } from '../../../../utils';
import { useMainContentSize } from '../../../../hooks/useMainContentSize';
import {
  AWSImage,
  IImage,
  ILabel,
  ImageDimensions,
  IPosition,
  SelectedWordsImage,
  WritingType,
} from '../../../../types';
import { IWordState } from '../../../../contexts/styledWordContext';
import { ReplaceImageModal } from '../../ReplaceImageModal';
import { EMERGENT } from '../../../../constants';
import { UndoRedoContext } from '../../../../contexts/undoRedoContext';
import { isUndoButtonClicked, isUndoOrRedoButtonClicked } from '../../../../utils/undoRedo';
import { DEFAULT_POSITION } from '../../../DraggableLabel/ContextDraggableLabel';

interface IWordsScreen {
  images: Array<IImage>;
  labels: Array<ILabel>;
  labelColor: string;
  editMode: boolean;
  styledWordState: IWordState;
  relatedImages: Array<AWSImage>;
  exemplarId: number;
  updateRelationships: (default_images: AWSImage[]) => void;
}

export const WordsScreen: React.FC<IWordsScreen> = (props): JSX.Element => {
  const [isReplaceImageModalOpen, setReplaceImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<SelectedWordsImage>({ id: '', sectionId: '' });

  const { images, labels, labelColor, editMode, styledWordState, relatedImages, exemplarId, updateRelationships } =
    props;

  const { state, dispatch, setLabels, setImages, saveWordsImageUpdates, saveLabelsUpdates } =
    useContext(WordsPageContext);
  const { undo, redo } = useContext(UndoRedoContext);
  const [x, y] = useMainContentSize();

  useEffect(() => {
    const allImages = !state.images.length ? images : state.images;
    const imagesToShow = getImagesToShow(allImages);

    const contextLabels = state.labels.length == 0 ? labels : state.labels;
    const labelsToShow = getLabelsToShow(contextLabels, imagesToShow, x, y);

    setImages(imagesToShow);
    setLabels(labelsToShow);
  }, []);

  const uploadImage = (
    imageUUIDtoReplace: string,
    default_uuid: string | undefined,
    sectionId: string,
    dimensions: ImageDimensions
  ) => {
    setSelectedImage({
      id: imageUUIDtoReplace,
      sectionId,
      default_uuid,
      dimensions,
    });
    setReplaceImageModal(true);
  };

  const replaceImage = (
    imageUUIDtoReplace: string,
    uuid: string,
    default_uuid: string | undefined,
    defaultImageDimensions: ImageDimensions,
    selectedImageDimensions: ImageDimensions
  ) => {
    const defaultUUID = default_uuid === undefined ? imageUUIDtoReplace : default_uuid;
    const payload = {
      imageUUIDtoReplace,
      uuid,
      default_uuid,
      defaultImageDimensions,
      selectedImageDimensions,
    };
    const state = { ...payload, default_uuid: defaultUUID };
    const prevState = {
      ...state,
      imageUUIDtoReplace: uuid,
      uuid: imageUUIDtoReplace,
    };

    saveWordsImageUpdates(prevState, state);
    dispatch({ type: ActionTypes.REPLACE_IMAGE, payload });
  };

  const handleLabelDragStop = (id: number, position: IPosition, text: string, selectionStart: number) => {
    const draggedLabel = state.labels.find((label: ILabel) => label.id === id) as ILabel;
    const isHidden = getCollidedLabel(position, state.images, { x, y });

    const updatedLabel = {
      ...draggedLabel,
      isFocussed: editMode,
      selectionRange: {
        width: getVerbWidth(text),
        selectionStart,
      },
      position,
      isHidden,
      text,
    };

    const restLabels = state.labels.filter((label: ILabel) => {
      return label.id !== id;
    });

    const payload = [...restLabels, updatedLabel] as Array<ILabel>;

    setLabels(payload);
    const labelPosition = draggedLabel.position;

    if (!isEqual(DEFAULT_POSITION, position) && isDraggableElementWasMoved(position, labelPosition)) {
      editMode && saveLabelsUpdates(state.labels, payload);
    }
  };

  const handleImageClick = (id: number) => {
    if (id !== 0) {
      const imageToTurn = state.images.find((image: IImage) => {
        return image.id === id;
      });
      const restImages = state.images.filter((image: IImage) => {
        return image.id !== id;
      });

      const imagesPayload = [...restImages, { ...imageToTurn, isHidden: !imageToTurn?.isHidden }] as Array<IImage>;
      const labelsPayload = state.labels.map(label => {
        const isHidden = getCollidedLabel(label.position, imagesPayload, { x, y });
        return { ...label, isHidden: isHidden };
      });
      setLabels(labelsPayload);
      setImages(imagesPayload);
    }
  };

  const handleLabelBlur = (id: number, event: FocusEvent<HTMLInputElement>) => {
    const eventTarget = event.target as HTMLInputElement;
    const { value, style, selectionStart } = eventTarget;

    if (event && isUndoOrRedoButtonClicked(event)) {
      return;
    }

    if (value.trim() === '') {
      handleRemoveElement(id, event);
      return;
    }

    const draggedLabel = state.labels.find((label: ILabel) => label.id === id) as ILabel;

    if (draggedLabel.text === value) {
      return;
    }

    const selectionRange = { width: style.width, selectionStart: selectionStart as number };
    const updatedLabel = { ...draggedLabel, isFocussed: false, selectionRange, text: value };

    const restLabels = state.labels.filter((label: ILabel) => label.id !== id);
    const allLabels = [...restLabels, updatedLabel] as ILabel[];
    const unFocussedLabels = allLabels.map((label: ILabel) => ({ ...label, isFocussed: false }));
    setLabels(unFocussedLabels);
    saveLabelsUpdates(state.labels, unFocussedLabels);
  };

  const handleRemoveElement = (id: number, event?: FocusEvent<HTMLInputElement>) => {
    if (event && isUndoOrRedoButtonClicked(event)) {
      isUndoButtonClicked(event) ? undo() : redo();
    }

    const restLabels = state.labels
      .filter((label: ILabel) => {
        label.selectionRange.selectionStart = label.text.length;
        return label.id !== id;
      })
      .map((label: ILabel) => (label.isFocussed ? { ...label, isFocussed: false } : label));

    const payload = [...restLabels] as Array<ILabel>;
    setLabels(payload);
    saveLabelsUpdates(state.labels, payload);
  };

  return (
    <WordsWrapper>
      {state.images
        .map((image: IImage) => ({ ...image, url: getExemplarImageURL(image.uuid, relatedImages) }))
        .map((image: IImage) => {
          const { x, y, height, width } = image.position;
          const { id, isHidden, url, key, uuid, default_uuid, editable } = image;
          return (
            <WordsImage
              isHidden={isHidden as boolean}
              key={id}
              url={url}
              y={y}
              x={x}
              height={height}
              width={width}
              handleClick={handleImageClick}
              id={id as number}
              uuid={uuid}
              default_uuid={default_uuid}
              imageKey={key}
              styledWordState={styledWordState}
              uploadImage={uploadImage}
              editMode={editMode && editable}
            />
          );
        })}
      {!styledWordState.imageTool.isVisible &&
        state.labels.map((label: ILabel) => {
          const { position, selectionRange, id, isManuallyHidden, isHighlighted, text } = label;

          if (!position) {
            return null;
          }

          const isHidden = getCollidedLabel(position, state.images, { x, y });

          return (
            <DraggableLabel
              labelColor={labelColor}
              key={uuid()}
              text={text}
              selectionRange={selectionRange}
              position={position}
              handleStop={handleLabelDragStop}
              handleBlur={handleLabelBlur}
              handleRemove={handleRemoveElement}
              isHidden={isHidden}
              isEditMode={editMode}
              isFocussed={label.isFocussed}
              isManuallyHidden={isManuallyHidden}
              isHighlighted={isHighlighted}
              id={id}
            />
          );
        })}
      {isReplaceImageModalOpen && (
        <ReplaceImageModal
          exemplarId={exemplarId}
          default_uuid={selectedImage.default_uuid}
          currentImageUUID={selectedImage.id}
          sectionId={selectedImage.sectionId}
          relatedImages={relatedImages}
          writingType={WritingType.exemplars}
          color={getStageColors(EMERGENT).main}
          setRelationships={updateRelationships}
          replaceImage={replaceImage}
          defaultImageDimensions={selectedImage.dimensions}
          onClose={() => setReplaceImageModal(false)}
        />
      )}
    </WordsWrapper>
  );
};
